import {ArrayTogglerBehaviorTypes} from '../../../hooks/useArrayToggler';

// keeping key names same as hermes request for ease of lookup
export const fixedFilters = {
  step_range: {
    name: 'step_range',
    type: ArrayTogglerBehaviorTypes.multiSelect,
    data: [
      {name: 'Upto ₹2000', key: {min: 0, max: 2000}},
      {name: '₹2001 - ₹4000', key: {min: 2001, max: 4000}},
      {name: '₹4001 - ₹6000', key: {min: 4001, max: 6000}},
      {name: '₹6001 - ₹8000', key: {min: 6001, max: 8000}},
      {name: '₹8000 +', key: {min: 8000, max: 8000}},
    ],
  },
  star: {
    name: 'star',
    type: ArrayTogglerBehaviorTypes.multiSelect,
    data: [
      {name: '5 star', key: 5},
      {name: '4 star', key: 4},
      {name: '3 star', key: 3},
      {name: '2 star', key: 2},
      {name: '1 star', key: 1},
    ],
  },
  range_ugc: {
    name: 'range_ugc',
    type: ArrayTogglerBehaviorTypes.singleSelect,
    data: [
      {name: '4+', key: [4, 5]},
      {name: '3.5+', key: [3.5, 5]},
      {name: '3+', key: [3, 5]},
    ],
  },
};

// Type as generic util
export const findFixedFilter = ({target, src, type}) => {
  const result = src.find(srcItem => {
    if (type === 'step_range') {
      return `${type}${srcItem.key.min}` === `${type}${target.min}`;
    }
    if (type === 'star') {
      return `${type}${srcItem.key}` === `${type}${target}`;
    }
    if (type === 'range_ugc') {
      return `${type}${srcItem.key[0]}` === `${type}${target[0]}`;
    }
    return false;
  });
  return result;
};

export function getFilterCategoryForHermesDataList(
  data: Array<{n: string; fd: {(key: string): any}}>,
): string {
  return Object.keys(data.find(item => item.fd && Object.keys(item.fd).length > 0)?.fd)[0];
}

export function updateFilterForSingleSelection(filterDictForItemUnderAction, appliedFilters) {
  const newAppliedFilterObj = {...appliedFilters};

  const filterKeyFromItemUnderAction = Object.keys(filterDictForItemUnderAction)[0];
  const filterValueFromItemUnderAction = filterDictForItemUnderAction[filterKeyFromItemUnderAction];

  const filterKeyToBeOperatedOn = Object.keys(appliedFilters)
    .map(appliedFilterKey => appliedFilterKey)
    .find(item => item === Object.keys(filterDictForItemUnderAction)[0]);

  if (!filterKeyToBeOperatedOn) {
    newAppliedFilterObj[filterKeyFromItemUnderAction] = filterValueFromItemUnderAction;
    return newAppliedFilterObj;
  }

  if (Array.isArray(newAppliedFilterObj[filterKeyFromItemUnderAction])) {
    const filterAlreadyHasItem = newAppliedFilterObj[filterKeyFromItemUnderAction].find(
      item => item.loc_id && filterValueFromItemUnderAction[0].loc_id === item.loc_id,
    );

    if (filterAlreadyHasItem) {
      newAppliedFilterObj[filterKeyFromItemUnderAction] = newAppliedFilterObj[
        filterKeyFromItemUnderAction
      ].filter(item => item.loc_id && item.loc_id !== filterAlreadyHasItem.loc_id);
    } else {
      newAppliedFilterObj[filterKeyFromItemUnderAction].push(filterValueFromItemUnderAction[0]);
    }
  } else {
    newAppliedFilterObj[filterKeyFromItemUnderAction] =
      newAppliedFilterObj[filterKeyFromItemUnderAction] === 0 ? 1 : 0;
  }

  return newAppliedFilterObj;
}

const fixedPopularFilters = {
  data: [
    {
      n: 'Free Cancellation Available',
      fd: {fc: 1},
      text: 'Free cancellation plans available. Cancel within the given time at no extra charges',
    },
    {
      n: 'Pay At Hotel Available',
      fd: {pah: 1},
      text: 'Pay at hotel plans available. Get a confirmed booking now and pay only at the hotel',
    },
    {
      n: 'Free Breakfast Included',
      fd: {fbfi: 1},
      text: 'You would be provided with breakfast at no extra cost',
    },
  ],
  names: {
    fc: 'Free Cancellation Available',
    pah: 'Pay At Hotel Available',
    fbfi: 'Free Breakfast Included',
  },
  symbols: ['fbfi', 'pah', 'fc'],
  resetValues: {
    fbfi: 0,
    fc: 0,
    pah: 0,
  },
};

export const quickFilterNameMap = {
  'Pay At Hotel': 'Pay At Hotel Available',
  'Free Breakfast': 'Free Breakfast Included',
  'Free Cancellation': 'Free Cancellation Available',
  'Book Now, Pay Later': 'Book Now, Pay Later Available',
};

export const rnplPopularFilter = {
  name: 'Book Now, Pay Later Available',
  symbol: 'isrnpl',
  resetValue: {isrnpl: 0},
  data: {
    n: 'Book Now, Pay Later Available',
    fd: {isrnpl: 1},
    text: 'Get a confirmed booking now and you would be charged later',
  },
};

export const getPopularFilterConfig = (isrnpl = 0) => {
  if (isrnpl) {
    return {
      data: [
        ...fixedPopularFilters.data.slice(0, 1),
        rnplPopularFilter.data,
        ...fixedPopularFilters.data.slice(1),
      ], // rnpl has to be added to the second index
      names: {...fixedPopularFilters.names, isrnpl: rnplPopularFilter.name},
      symbols: [
        ...fixedPopularFilters.symbols.slice(0, 1),
        rnplPopularFilter.symbol,
        ...fixedPopularFilters.symbols.slice(1),
      ],
      resetValues: {
        ...fixedPopularFilters.resetValues,
        ...rnplPopularFilter.resetValue,
      },
    };
  }

  return fixedPopularFilters;
};

export function getSRPAppliedFiltersText(appliedFilter) {
  // This returns the concatenated string for all the currently applied filters with name and values
  // use ",# " to separate between various filters and "," for different values of same filter
  let filterText = '';

  for (const [filter_name, filter_val] of Object.entries(appliedFilter)) {
    if (
      (!Array.isArray(filter_val) && filter_val) ||
      (Array.isArray(filter_val) && filter_val.length > 0)
    ) {
      let formatted_filter_val = '';
      let formatted_filter_name = Object.keys(fixedPopularFilters.names).includes(filter_name)
        ? fixedPopularFilters.names[filter_name]
        : filter_name;
      if (filter_name == 'step_range') {
        formatted_filter_name = 'Price Range';
        fixedFilters.step_range.data.map(item => {
          if (filter_val.some(val => val === item.key)) {
            formatted_filter_val += item.name + ',';
          }
        });
      } else if (['location_search', 'transit', 'landmark'].includes(filter_name)) {
        filter_val.map(item => {
          formatted_filter_val = formatted_filter_val + item.n + ',';
        });
      } else {
        formatted_filter_val = filter_val + ',';
      }
      filterText += formatted_filter_name + ':' + formatted_filter_val + '# ';
    }
  }
  return filterText;
}
